import { Box, Flex } from '@chakra-ui/react';
import { useFieldArray } from 'react-hook-form';
import { useEffect, useState } from 'react';

import { CloseIcon, EditIcon, PlusIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import AttachmentImage from 'Component/attachment-image';
import { TextMedium, TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import InputComponent from 'UiKit/input';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputUploadFile from 'UiKit/input-upload-file';
import usePostUserImage from 'Domain/brief-live/usePostUserImage';

import SelectProductModal from 'View/brief-live/component/select-product-modal';
import { useBriefGiveawayFormState } from 'View/brief-live/state/giveaway-form';

import styles from './styles.module.css';

const InputProductManual = ({ product, productIndex, update, t, onRemove, onSubmit }) => {
  const { mutateAsync: uploadImage } = usePostUserImage();
  const isDisabled = !product?.imageKey || !product?.productName;
  return (
    <Box width={'100%'}>
      <Flex gap={'12px'} width={'100%'}>
        <InputUploadFile
          onChange={async (file) => {
            const result = await uploadImage({ file });
            update(productIndex, {
              ...product,
              imagePath: result?.imagePath,
              imageKey: result?.key,
            });
          }}
          initialPreview={product?.imagePath || null}
          height="65px"
          width="64px"
        />
        <Box width={'100%'}>
          <InputComponent
            placeholder={t('formGiveaway.productName')}
            height="28px"
            onChange={(value) => {
              update(productIndex, {
                ...product,
                productName: value,
              });
            }}
            value={product?.productName}
            margin={'0 0 8px 0'}
            width={'100%'}
          />
          <InputComponent
            placeholder={t('formGiveaway.skuId')}
            height="28px"
            onChange={(value) => {
              update(productIndex, {
                ...product,
                sku: value,
              });
            }}
            value={product?.sku}
            width={'50%'}
          />
        </Box>
      </Flex>
      <Flex marginTop={'16px'} justifyContent={'end'} gap={'8px'}>
        <ButtonOutline height={'28px'} borderRadius="2px" onClick={onRemove}>
          {t('formGiveaway.cancelButton')}
        </ButtonOutline>
        <ButtonSolid height={'28px'} borderRadius="2px" isDisabled={isDisabled} disabled={isDisabled} onClick={onSubmit}>
          {t('formGiveaway.saveButton')}
        </ButtonSolid>
      </Flex>
    </Box>
  );
};

const RequiredProductsFields = ({ t, control, giveawayIndex, watchedGiveaways, isEditing, watchedBrand }) => {
  const {
    fields: requiredProducts,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: `giveaways.${giveawayIndex}.rules.requiredProducts`,
  });

  const { state, updateState, updateFilterState, filterState } = useBriefGiveawayFormState();
  const isSelectProductModalOpen = state.isOpenModalSelectProduct && state.activeGiveawayIndex === giveawayIndex;
  const [isEdit, setIsEdit] = useState(false);

  const handleSelectProductConfirm = (items) => {
    items?.forEach((item) => {
      append({
        sku: item?.skuCode,
        productName: item?.productName,
        imagePath: item?.image,
      });
    });
  };

  const watchedGiveaway = watchedGiveaways[giveawayIndex];

  const selectedProducts =
    watchedGiveaways?.[giveawayIndex]?.rules?.requiredProducts
      ?.filter((product) => !product?.isManual)
      .map((product) => {
        return {
          ...product,
          value: product?.sku,
        };
      }) || [];

  useEffect(() => {
    const foundEmptyRequiredManualProduct = watchedGiveaway?.rules?.requiredProducts?.some(
      (product) => product?.isManual && (!product?.imageKey || !product?.productName),
    );

    setIsEdit(foundEmptyRequiredManualProduct);
  }, [watchedBrand, watchedGiveaway]);

  return (
    <>
      <Box className={styles['required-products__container']}>
        <TextRegular className={styles['required-products__text--title']}>
          <Condition
            statement={watchedGiveaway?.rules?.type === 'MINIMUM_PURCHASE'}
            children={t('formGiveaway.withProductPurchase')}
            fallback={t('formGiveaway.productPurchase')}
          />
        </TextRegular>
        <Iteration
          items={requiredProducts}
          render={(product, productIndex) => {
            const watchedProduct = watchedGiveaways?.[giveawayIndex]?.rules?.requiredProducts?.[productIndex] || {};
            const images = watchedProduct?.imagePath ? [{ url: watchedProduct?.imagePath }] : [];

            return (
              <Condition
                statement={!product.isEdit}
                fallback={
                  <InputProductManual
                    control={control}
                    product={product}
                    productIndex={productIndex}
                    update={update}
                    t={t}
                    onRemove={() => {
                      remove(productIndex);
                      setIsEdit(false);
                    }}
                    onSubmit={() => {
                      setIsEdit(false);
                      update(productIndex, { ...product, isEdit: false });
                    }}
                  />
                }
              >
                <Flex key={product.id} className={styles['required-product__item']} gap="24px" alignItems="flex-start" width={'100%'}>
                  <Flex height="45px" width="45px" justifyContent={'center'} alignItems={'center'}>
                    <AttachmentImage images={images} />
                  </Flex>
                  <Box className={styles['required-products__box--fields']}>
                    <TextMedium className={styles['required-products__name--text']}>{watchedProduct?.productName}</TextMedium>
                    <TextRegular>{watchedProduct?.sku}</TextRegular>
                  </Box>
                  {requiredProducts.length > 1 && !isEditing && (
                    <Flex gap={'8px'}>
                      <Condition statement={product.isManual}>
                        <ButtonOutline
                          borderWidth="1.5px"
                          color={isEdit ? 'var(--color-neutral50)' : 'var(--color-info70)'}
                          padding="6px"
                          borderRadius="2px"
                          width="28px"
                          height="28px"
                          isDisabled={isEdit}
                          onClick={() => {
                            setIsEdit(true);
                            update(productIndex, {
                              ...product,
                              isEdit: true,
                            });
                          }}
                        >
                          <EditIcon color={isEdit ? 'var(--color-neutral50)' : 'var(--color-info70)'} size="11px" />
                        </ButtonOutline>
                      </Condition>
                      <ButtonOutline
                        borderWidth="1.5px"
                        color={isEdit ? 'var(--color-neutral50)' : 'var(--color-error50)'}
                        padding="6px"
                        borderRadius="2px"
                        width="28px"
                        height="28px"
                        isDisabled={isEdit}
                        onClick={() => remove(productIndex)}
                      >
                        <CloseIcon color={isEdit ? 'var(--color-neutral50)' : 'var(--color-error50)'} size="11px" />
                      </ButtonOutline>
                    </Flex>
                  )}
                </Flex>
              </Condition>
            );
          }}
        />
        <Flex gap={'16px'}>
          <ButtonSolid
            leftIcon={<PlusIcon width="14px" height="14px" color="var(--color-semantic-white)" />}
            onClick={() =>
              updateState({
                isOpenModalSelectProduct: true,
                activeGiveawayIndex: giveawayIndex,
              })
            }
            disabled={isEdit}
            isDisabled={isEdit}
          >
            <TextSemiBold className={styles['required-product__text--button-add']}>{t('selectProductModal.addProduct')}</TextSemiBold>
          </ButtonSolid>
          <ButtonOutline
            leftIcon={<PlusIcon width="14px" height="14px" color="var(--color-info70)" />}
            onClick={() => {
              append({ productName: '', sku: '', imagePath: '', isEdit: true, isManual: true });
              setIsEdit(true);
            }}
            disabled={isEdit}
            isDisabled={isEdit}
          >
            <TextSemiBold className={styles['required-product__text--button-add-manual']}>{t('selectProductModal.addManualProduct')}</TextSemiBold>
          </ButtonOutline>
        </Flex>
      </Box>
      <SelectProductModal
        isOpen={isSelectProductModalOpen}
        updateState={updateState}
        filterState={filterState}
        updateFilterState={updateFilterState}
        onConfirm={handleSelectProductConfirm}
        existingProducts={selectedProducts}
        t={t}
      />
    </>
  );
};

export default RequiredProductsFields;
